import { sections, Section, Subsection } from '../data/sections'

export type SearchResult = {
  sectionId: string
  sectionNumber: number
  sectionTitle: string
  sectionIcon: any
  subsectionId: string
  subsectionTitle: string
  snippet: string
  score: number
}

function buildSnippet(body: string, query: string, contextChars = 80): string {
  const lower = body.toLowerCase()
  const q = query.toLowerCase()
  const idx = lower.indexOf(q)
  if (idx === -1) {
    // Fallback: first ~160 chars
    const clean = body.replace(/\*\*/g, '').replace(/\n+/g, ' ').trim()
    return clean.length > 160 ? clean.slice(0, 160) + '…' : clean
  }
  const start = Math.max(0, idx - contextChars)
  const end = Math.min(body.length, idx + query.length + contextChars)
  let slice = body
    .slice(start, end)
    .replace(/\*\*/g, '')
    .replace(/\n+/g, ' ')
    .trim()
  if (start > 0) slice = '…' + slice
  if (end < body.length) slice = slice + '…'
  return slice
}

export function searchSections(rawQuery: string, limit = 8): SearchResult[] {
  const query = rawQuery.trim().toLowerCase()
  if (!query) return []

  const results: SearchResult[] = []

  for (const section of sections) {
    const sectionTitleLower = section.title.toLowerCase()
    const sectionSummaryLower = section.summary.toLowerCase()
    const sectionNumberStr = String(section.number)

    for (const sub of section.subsections) {
      const subTitleLower = sub.title.toLowerCase()
      const bodyLower = (sub.body || '').toLowerCase()

      let score = 0
      // Strong matches
      if (subTitleLower.includes(query)) score += 50
      if (sectionTitleLower.includes(query)) score += 30
      if (sectionSummaryLower.includes(query)) score += 10
      if (sectionNumberStr === query) score += 25

      // Body matches — count occurrences
      if (bodyLower.includes(query)) {
        const occurrences = bodyLower.split(query).length - 1
        score += 8 + Math.min(occurrences, 5) * 2
      }

      // Callouts
      if (sub.callouts) {
        for (const c of sub.callouts) {
          if (
            c.title.toLowerCase().includes(query) ||
            c.content.toLowerCase().includes(query)
          ) {
            score += 12
          }
        }
      }

      // Workflow diagrams
      if (sub.workflowDiagrams) {
        for (const d of sub.workflowDiagrams) {
          if (d.title.toLowerCase().includes(query)) score += 10
          for (const step of d.steps) {
            if (step.toLowerCase().includes(query)) score += 6
          }
        }
      }

      if (score > 0) {
        const snippetSource =
          sub.body ||
          sub.callouts?.[0]?.content ||
          sub.workflowDiagrams?.[0]?.steps.join(' ') ||
          section.summary
        results.push({
          sectionId: section.id,
          sectionNumber: section.number,
          sectionTitle: section.title,
          sectionIcon: section.icon,
          subsectionId: sub.id,
          subsectionTitle: sub.title,
          snippet: buildSnippet(snippetSource, query),
          score,
        })
      }
    }
  }

  results.sort((a, b) => b.score - a.score)
  return results.slice(0, limit)
}

export function highlightMatch(
  text: string,
  query: string,
): (string | { match: string })[] {
  if (!query.trim()) return [text]
  const parts: (string | { match: string })[] = []
  const lower = text.toLowerCase()
  const q = query.toLowerCase()
  let i = 0
  while (i < text.length) {
    const idx = lower.indexOf(q, i)
    if (idx === -1) {
      parts.push(text.slice(i))
      break
    }
    if (idx > i) parts.push(text.slice(i, idx))
    parts.push({ match: text.slice(idx, idx + q.length) })
    i = idx + q.length
  }
  return parts
}
